<script setup lang="ts">
  import { ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Plus } from "lucide-vue-next";
  import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
  import { Button } from "@/components/ui/button";
  import PopulationEditor from "@/components/Survey/ResultsV2/PopulationEditor.vue";
  import { useFilterableCards } from "@/components/Survey/ResultsV2/useFilterableCards";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useGridsStore } from "@stores/survey/grids/grids";
  import { togglePopulationFilter, type PopulationFilters } from "@/utils/togglePopulationFilter";

  interface Draft {
    token: string | null;
    name: string;
    color: string | null;
    filters: PopulationFilters;
  }

  const open = defineModel<boolean>("open", { required: true });
  const props = defineProps<{ token: string | null }>();

  const gridsStore = useGridsStore();
  const { populations } = storeToRefs(gridsStore);
  const resultsStore = useResultsV2Store();
  const { filterableCards, fetchFilterableCards } = useFilterableCards();

  const emptyDraft = (): Draft => ({ token: null, name: "", color: null, filters: {} });

  const draft = ref<Draft>(emptyDraft());
  const confirmingDelete = ref(false);
  const saving = ref(false);

  const edit = (token: string | null) => {
    const population = populations.value.find((item) => item.token === token);
    draft.value = population
      ? { token: population.token, name: population.name, color: population.color, filters: { ...population.filters } }
      : emptyDraft();
    confirmingDelete.value = false;
  };

  watch(open, (isOpen) => {
    if (!isOpen) return;
    fetchFilterableCards();
    edit(props.token);
  });

  const refresh = () => Promise.all([gridsStore.fetchPopulations(), gridsStore.fetch()]);

  const save = async () => {
    saving.value = true;
    try {
      const { token, ...population } = draft.value;
      const saved = token
        ? await resultsStore.updateSavedPopulation(token, population)
        : await resultsStore.createSavedPopulation(population);
      await refresh();
      if (saved) edit(saved.token);
    } finally {
      saving.value = false;
    }
  };

  const remove = async () => {
    if (!draft.value.token) return;
    await resultsStore.deleteSavedPopulation(draft.value.token);
    await refresh();
    edit(null);
  };
</script>

<template>
  <Sheet v-model:open="open">
    <SheetContent
      side="right"
      class="flex w-[640px] flex-col sm:max-w-[640px]">
      <SheetHeader class="flex-row items-center gap-3 px-4 pt-4 pb-2">
        <SheetTitle>Populations</SheetTitle>
      </SheetHeader>

      <div class="flex min-h-0 flex-1">
        <div class="flex w-[200px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r px-2 py-2">
          <button
            v-for="population in populations"
            :key="population.token"
            type="button"
            class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-13"
            :class="draft.token === population.token ? 'bg-muted font-medium' : 'hover:bg-muted/60'"
            @click="edit(population.token)">
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: population.color || 'transparent' }" />
            <span class="min-w-0 flex-1 truncate">{{ population.name }}</span>
          </button>
          <Button
            variant="outline"
            size="sm"
            class="mt-1 justify-start border-dashed font-normal text-muted-foreground"
            :class="{ 'bg-muted': !draft.token }"
            @click="edit(null)">
            <Plus class="shrink-0" />
            New
          </Button>
        </div>

        <div class="flex min-h-0 min-w-0 flex-1 flex-col">
          <PopulationEditor
            :key="draft.token ?? 'new'"
            v-model:name="draft.name"
            :color="draft.color"
            :filters="draft.filters"
            :cards="filterableCards"
            :can-save="!saving && !!draft.name.trim()"
            :can-delete="!!draft.token"
            save-label="Save"
            @update:color="(color) => (draft.color = color)"
            @toggle-filter="(questionId, code) => (draft.filters = togglePopulationFilter(draft.filters, questionId, code))"
            @save="save"
            @delete="confirmingDelete = true" />
        </div>
      </div>

      <div
        v-if="confirmingDelete"
        class="flex items-center gap-2 border-t bg-destructive/5 px-4 py-3">
        <span class="min-w-0 flex-1 text-13">
          Delete <span class="font-medium">"{{ draft.name }}"</span>? Its grid rows go with it.
        </span>
        <Button
          variant="ghost"
          size="sm"
          @click="confirmingDelete = false">
          Cancel
        </Button>
        <Button
          size="sm"
          class="bg-destructive text-white hover:bg-destructive/90"
          @click="remove">
          Delete
        </Button>
      </div>
    </SheetContent>
  </Sheet>
</template>
